import { useEffect } from 'react'

const INTERVAL = 3000        // auto-advance cadence
const RESUME_AFTER = 9000    // quiet period after a manual click

/**
 * Drives the framework panel in #compliance ([data-fw-carousel]).
 *
 * Five slides share one grid cell (see the .fw-deck rules) and are swapped by
 * toggling .is-active - the fade/rise lives entirely in CSS, so an auto
 * advance and a click animate identically. Inactive slides are hidden with
 * visibility, not [hidden]/display:none, which keeps the deck's height equal
 * to the tallest slide so nothing reflows as it cycles.
 *
 * Auto-advance pauses when the panel is scrolled out of view, while keyboard
 * focus is inside it, when the tab is backgrounded, and for RESUME_AFTER ms
 * after a manual click. It never starts under prefers-reduced-motion.
 *
 * It deliberately does NOT pause on hover. The panel is close to full-viewport
 * on desktop, so a resting cursor sits over it almost the whole time it is on
 * screen - the pause then fired continuously and the carousel read as broken,
 * something you could only advance by clicking. ProductsCarousel.jsx reached
 * the same conclusion for the same reason. Reading time is still protected:
 * clicking a tab or arrow buys RESUME_AFTER ms, and tabbing in holds it
 * indefinitely until focus leaves.
 *
 * Mirrors ProductsCarousel.jsx: idempotent per panel via a data flag, and
 * re-scanned on DOM mutations because pentabay-pages.js appends page mains
 * after React has rendered.
 */
export default function FrameworksCarousel() {
  useEffect(() => {
    const timers = new Set()
    const observers = new Set()

    function init(panel) {
      if (panel.dataset.fwReady === '1') return
      const slides = [...panel.querySelectorAll('.fw-slide')]
      const tabs = [...panel.querySelectorAll('.fw-tab')]
      if (!slides.length) return
      panel.dataset.fwReady = '1'

      const reduce = typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches

      let i = Math.max(0, slides.findIndex(s => s.classList.contains('is-active')))
      let pausedUntil = 0
      // Gated on visibility so the deck isn't silently cycling while it's off
      // screen - otherwise scrolling down to it lands you mid-sequence on a
      // slide you never saw start. Defaults to true so the carousel still runs
      // if IntersectionObserver is unavailable.
      let onScreen = true

      const show = n => {
        i = (n + slides.length) % slides.length
        slides.forEach((s, k) => s.classList.toggle('is-active', k === i))
        tabs.forEach((t, k) => {
          t.classList.toggle('is-active', k === i)
          t.setAttribute('aria-selected', k === i ? 'true' : 'false')
          // Only the selected tab stays in the tab sequence; arrow keys move
          // between them, which is the expected tablist interaction.
          t.tabIndex = k === i ? 0 : -1
        })
      }

      // A manual move pauses the cycle briefly, so the slide someone just
      // chose doesn't get pulled out from under them.
      const drive = n => { pausedUntil = performance.now() + RESUME_AFTER; show(n) }

      tabs.forEach((t, k) => t.addEventListener('click', () => drive(k)))
      panel.querySelector('.fw-prev')?.addEventListener('click', () => drive(i - 1))
      panel.querySelector('.fw-next')?.addEventListener('click', () => drive(i + 1))

      panel.querySelector('.fw-tabs')?.addEventListener('keydown', e => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
        e.preventDefault()
        drive(e.key === 'ArrowRight' ? i + 1 : i - 1)
        tabs[i].focus()
      })

      panel.addEventListener('focusin', () => { pausedUntil = Infinity })
      panel.addEventListener('focusout', () => {
        if (!panel.contains(document.activeElement)) pausedUntil = performance.now() + RESUME_AFTER
      })

      show(i)

      if (reduce) return

      // A slim sliver counts as visible: the panel is taller than most
      // viewports, so requiring a large ratio would keep it paused even while
      // it fills the screen.
      let io = null
      if (typeof IntersectionObserver === 'function') {
        onScreen = false
        io = new IntersectionObserver(
          entries => { onScreen = entries.some(e => e.isIntersecting) },
          { threshold: 0.01 },
        )
        io.observe(panel)
        observers.add(io)
      }

      const timer = setInterval(() => {
        if (!panel.isConnected) {
          clearInterval(timer); timers.delete(timer)
          io?.disconnect(); observers.delete(io)
          return
        }
        if (!onScreen || document.hidden || performance.now() < pausedUntil) return
        show(i + 1)
      }, INTERVAL)
      timers.add(timer)
    }

    const run = () => document.querySelectorAll('[data-fw-carousel]').forEach(init)
    run()
    const mo = new MutationObserver(run)
    mo.observe(document.body, { childList: true, subtree: true })
    return () => {
      mo.disconnect()
      timers.forEach(clearInterval)
      observers.forEach(o => o?.disconnect())
    }
  }, [])

  return null
}
